// Requisitions fetched from ATLAS: VLA asking for more information before it decides an
// application or extension. Each is kept on its matter (matter.requisitions) until it is
// answered, and the Aid Planner shows every open one in a banner.

const REQUISITION_STATUS = "Requisition to be answered";
const SETTLED_STATUS = /^(Approved|Aid granted|Refused|Aid refused)/i;

// "26A902979/0" and "26a902979" are the same grant.
export function normaliseAidNumber(value = "") {
  return String(value || "").toUpperCase().replace(/\s+/g, "").replace(/\/\d+$/, "");
}

const requisitionId = (req) => [normaliseAidNumber(req.fileId), req.extension || "0", req.sent || ""].join("|");

const isOpen = (req) => req && !req.answered;

// Records a requisition read from ATLAS on its matter. While it is open, a matter whose
// application is still undecided shows "Requisition to be answered"; the status it had
// is kept so it comes back once the requisition is answered.
export function applyRequisition(matter, req, { now = new Date() } = {}) {
  const id = requisitionId(req);
  const existing = (matter.requisitions || []).find((r) => r.id === id);
  const status = matter.aid?.applicationStatus || "";
  const record = {
    id,
    fileId: req.fileId || "",
    extension: req.extension || "0",
    sent: req.sent || "",
    dueOn: req.dueOn || "",
    atlasStatus: req.status || "",
    answered: Boolean(req.answered || existing?.answered),
    answeredAt: existing?.answeredAt || (req.answered ? now.toISOString() : ""),
    receivedAt: existing?.receivedAt || now.toISOString(),
    statusBefore: existing?.statusBefore ?? (status === REQUISITION_STATUS ? "" : status),
  };
  const requisitions = [...(matter.requisitions || []).filter((r) => r.id !== id), record];
  const next = { ...matter, requisitions };
  return withRequisitionStatus(next);
}

export function markRequisitionAnswered(matter, id, { now = new Date() } = {}) {
  const requisitions = (matter.requisitions || []).map((r) =>
    r.id === id ? { ...r, answered: true, answeredAt: now.toISOString() } : r,
  );
  return withRequisitionStatus({ ...matter, requisitions });
}

function withRequisitionStatus(matter) {
  const open = (matter.requisitions || []).filter(isOpen);
  const status = matter.aid?.applicationStatus || "";
  if (open.length && !SETTLED_STATUS.test(status)) {
    return { ...matter, aid: { ...matter.aid, applicationStatus: REQUISITION_STATUS } };
  }
  if (!open.length && status === REQUISITION_STATUS) {
    const before = [...(matter.requisitions || [])].reverse().find((r) => r.statusBefore)?.statusBefore;
    return { ...matter, aid: { ...matter.aid, applicationStatus: before || "Pending" } };
  }
  return matter;
}

// Every open requisition across these matters, soonest due first.
export function openRequisitions(matters = []) {
  return matters
    .flatMap((matter) => (matter.requisitions || []).filter(isOpen).map((req) => ({
      ...req,
      matterId: matter.matterId,
      clientName: matter.client?.fullName || "",
    })))
    .sort((a, b) => (a.dueOn || "9999").localeCompare(b.dueOn || "9999"));
}

// Whole days from `today` to the due date (negative once overdue), or null with no date.
export function daysUntilDue(dueOn, today = new Date()) {
  if (!dueOn) return null;
  const [y, m, d] = dueOn.split("-").map(Number);
  const due = Date.UTC(y, m - 1, d);
  const now = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((due - now) / 86400000);
}

// The matter a requisition belongs to: same aid number first, then the same client name.
export function findRequisitionMatter(matters = [], req) {
  const aid = normaliseAidNumber(req.fileId);
  const byAid = aid && matters.find((m) => normaliseAidNumber(m.aid?.aidNumber) === aid);
  if (byAid) return byAid;
  const name = String(req.clientName || "").toLowerCase().replace(/[^a-z]/g, "");
  if (!name) return null;
  return matters.find((m) => {
    const other = String(m.client?.fullName || "").toLowerCase().replace(/[^a-z]/g, "");
    if (other === name) return true;
    // ATLAS writes "SURNAME, GIVEN" on the inbox but "GIVEN SURNAME" on the requisition.
    const words = String(req.clientName || "").toLowerCase().split(/[\s,]+/).filter(Boolean).sort().join("");
    const otherWords = String(m.client?.fullName || "").toLowerCase().split(/[\s,]+/).filter(Boolean).sort().join("");
    return words && words === otherWords;
  }) || null;
}
